<template>
    <div :class="['media-picker', { 'multiupload': multiupload }]">
        <label :class="['drop', {'is-active': dragOver, 'only-upload': !supportsMediaLibrary}]"
            @dragover.prevent.stop="onDragOver"
            @dragleave.prevent.stop="dragOver = false"
            @drop.prevent.stop="onDrop"
        >
            <input ref="fileInput" type="file" accept="image/*" @change="onChange" :multiple="multiupload">

            <div class="drop-wrap">
                <div :class="['upload-section', {'is-visually-hidden': progressBarActive || !canUpload}]">
                    <i class="icon upload">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path class="primary" d="M19 19H1c-.6 0-1-.4-1-1v-5h2v4h16v-4h2v5c0 .6-.4 1-1 1z"/><path class="secondary" d="M15.7 6.3l-5-5c-.4-.4-1-.4-1.4 0l-5 5c-.4.4-.4 1 0 1.4s1 .4 1.4 0L9 4.4V13c0 .6.4 1 1 1s1-.4 1-1V4.4l3.3 3.3c.2.2.4.3.7.3s.5-.1.7-.3c.4-.4.4-1 0-1.4z"/></svg>
                    </i>

                    <div class="help">
                        {{ $c__('verb in infinitive form', 'Upload image') }}<br>
                        <span class="uppercase">jpg, gif, png, svg</span>
                    </div>
                </div>

                <div :class="['other-section', {'is-visually-hidden': progressBarActive || !canUpload}]" v-if="supportsMediaLibrary">
                    <button class="btn grey-light textual block icons-button" type="button"
                        v-if="supportedMediaTypes.includes('icon')"
                        @click.prevent.stop="$bus.$emit('openMediaLibrary', { supportedMediaTypes, multiupload, activeTab: 'icons' }, context)">
                        <span class="btn-wrap">
                            <i class="icon plus-circle shrink-0"></i>
                            <span class="text">{{ $c__('verb in infinitive form', 'Select icon') }}</span>
                        </span>
                    </button>

                    <button class="btn grey-light textual block library-button" type="button"
                        @click.prevent.stop="$bus.$emit('openMediaLibrary', { supportedMediaTypes, multiupload }, context)">
                        <span class="btn-wrap">
                            <svg-icon name="search" classes="shrink-0"></svg-icon>
                            <span class="text">{{ $c__('verb in infinitive form', 'Search for image') }}</span>
                        </span>
                    </button>
                </div>

                <progress-bar
                    :progress="progressBarValue"
                    v-show="progressBarActive"
                    @finished="progressBarFinished"
                    @transitioned="progressBarValue = progressBarActive == false ? 0 : progressBarValue"
                >
                </progress-bar>
            </div>
        </label>

        <medium inline-template v-if="medium" class="absolute" v-bind:medium="medium" :fit="fit" thumb="thumb">
            <transition :name="transition">
                <div :class="['media-holder', {'hover': showDeleteDialog}]">
                    <div :class="['media', {'fit' : medium.type == 'icon' || fit}, {'narrow' : medium.is_narrow}]" @click="openEditor">
                        <div class="img" :style="{ backgroundImage: `url('${mediumThumbnail}')` }">
                            <div class="overlay">
                                <div class="actions">
                                    <button class="btn-link small white narrow margin-right-mini" type="button" v-if="medium.type != 'icon'">{{ $t__('Edit') }}</button>
                                    <button class="btn-link small negative color-white narrow margin-left-auto" type="button" @click.prevent.stop="showDeleteDialog = true">{{ $t__('Delete') }}</button>
                                </div>

                                <transition name="shake">
                                    <div class="dialog" v-if="showDeleteDialog" @click.prevent.stop>
                                        <div class="bg" :style="{ backgroundImage: `url('${mediumThumbnail}')` }"></div>

                                        <div class="body">
                                            <i class="icon current-color">!</i>
                                            {{ $t__('Are you sure to delete this media?') }}
                                        </div>

                                        <div class="footer">
                                            <button class="btn-link small white narrow" type="button" @click.prevent.stop="showDeleteDialog = false">
                                                {{ $t__('Cancel') }}
                                            </button>

                                            <button :class="['btn', 'small', 'negative', 'narrow', { 'is-loading': isDeleting }]" type="button" @click.stop="onDelete()">
                                                {{ $t__('Delete') }}
                                            </button>
                                        </div>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>

                    <div v-if="medium.title || medium.file_name" class="info" @click="openEditor">
                        <div v-if="sortable" class="dragger on-hover"></div>
                        <span>{{ medium.title ? medium.title : medium.file_name }}</span>
                    </div>

                </div>
            </transition>
        </medium>

        <notification
            :show="hasNotification && !progressBarActive"
            variant="attention background-none small has-icon absolute"
            :auto-dismiss="true"
            :message="notificationMessage"
            @dismissed="onNotificationDismiss"
        />
    </div>
</template>

<script>

    import Medium from './Medium';
    import ProgressBar from './ProgressBar';
    import Notification from './Notification';
    import SvgIcon from './SvgIcon';

    export default {

        name: 'MediaPicker',

        props: {
            context: '',
            medium: null,
            multiupload: { type: Boolean, default: false },
            supportedMediaTypes: { type: Array, default: () => (['unsplash', 'upload'])},
            supportsMediaLibrary:  { type: Boolean, default: true },
            fit: false,
            thumb: {type: String, default: 'square'} // or original
        },

        data() {
            return {
                dragOver: false,
                progressBarValue: 0,
                progressBarActive: false,
                canUpload: true,
                notification: null
            }
        },

        components: { ProgressBar, Medium, Notification, SvgIcon },

        created() {
            this.canUpload = this.multiupload ? true : (this.medium ? false : true);
        },

        mounted() {
            this.$bus.$on('update-media-progressbar', this.maybeUpdateProgressBar);
        },

        computed: {
            hasNotification() {
                return this.notification ? true : false;
            },
            notificationMessage() {
                if (this.notification && this.notification.message) {
                    return this.notification.message;
                }
                return '';
            },
        },

        watch: {
            medium(val, oldVal) {
                if (!val) {
                    this.canUpload = true;
                }
            }
        },

        created() {
            this.canUpload = this.multiupload ? true : (this.medium ? false : true);

            this.$bus.$on('adding-media-failed', this.maybeDisplayNotification);
        },

        methods: {
            maybeUpdateProgressBar(context, value) {
                if (context == this.context) {
                    this.progressBarActive = true;
                    this.progressBarValue = value;
                }
            },

            maybeDisplayNotification(error, context) {
                // determine whether notification was emited for this context
                if (this.context == context) {
                    this.handleErrorResponse(error);
                }
            },

            utils() {
                return utils;
            },

            onChange(e) {
                let files = (e.target.files || e.dataTransfer.files);
                if (!files) return;

                this.$bus.$emit('setMediaContext', this.context);
                this.$bus.$emit('use-media-from-media-picker', files);
            },

            onDrop(e) {
                this.dragOver = false;
                this.onChange(e);
            },

            onDragOver(e) {
                e.dataTransfer.dropEffect = 'copy';
                this.dragOver = true;
            },

            progressBarFinished() {
                this.$refs.fileInput.value = '';

                this.progressBarActive = false;

                if (!this.multiupload || this.hasNotification) {
                    this.canUpload = false;
                }

                if (!this.hasNotification) this.$emit('upload-finished');
            },

            handleErrorResponse(errorResponse) {
                if(errorResponse.response){
                    if(errorResponse.response.status == 413){
                        this.notification = {message: this.$t__('Uploaded file is too large')}
                    }else if(errorResponse.response.status == 422){
                        this.notification = {message: (errorResponse.response.data.errors.image)[0]}
                    }else if(errorResponse.response.status >= 500){
                        this.notification = {message: this.$t__('Something is wrong with your image :/')}
                    }
                }
            },

            clearNotifications() {
                this.notification = null;
            },

            onNotificationDismiss() {
                this.clearNotifications();
                this.canUpload = true;
            }
        }
    }
</script>
